/* HV a11y-patch.js — universal WCAG 2.1 AA runtime defenses.
   Designed to be safe to run on ANY HV site (HTML / Next.js client-rendered / Shopify).
   No dependencies. No side effects beyond DOM patches. Idempotent. */
(function () {
  'use strict';

  if (typeof document === 'undefined') return;

  // Run once
  if (window.__hvA11yPatchApplied) return;
  window.__hvA11yPatchApplied = true;

  var run = function () {
    try {
      addSkipLink();
      labelIconOnlyControls();
      ensureMainLandmark();
      syncHtmlLang();
      labelIframes();
      makeScrollableRegionsFocusable();
    } catch (e) {
      if (window.console && console.error) {
        console.error('[hv-a11y-patch]', e);
      }
    }
  };

  // 1. Skip-to-content link injected as first focusable element.
  function addSkipLink() {
    if (document.querySelector('.hv-skip-link')) return;
    var target = document.querySelector('main, [role="main"], #main');
    if (!target) {
      // Will be created by ensureMainLandmark; defer.
      return;
    }
    if (!target.id) target.id = 'main';
    var link = document.createElement('a');
    link.className = 'hv-skip-link';
    link.href = '#' + target.id;
    link.textContent = 'Skip to main content';
    document.body.insertBefore(link, document.body.firstChild);
  }

  // 2. Add aria-label to <a> and <button> that contain only an icon (svg/img-decorative).
  // Heuristic: if the accessible name is empty AND the element contains only SVG/IMG/I, label it.
  function labelIconOnlyControls() {
    var controls = document.querySelectorAll(
      'a:not([aria-label]):not([aria-labelledby]), button:not([aria-label]):not([aria-labelledby]), [role="button"]:not([aria-label]):not([aria-labelledby])'
    );
    for (var i = 0; i < controls.length; i++) {
      var el = controls[i];

      // Skip if it has visible text content
      var visibleText = (el.textContent || '').replace(/\s+/g, '').trim();
      if (visibleText.length > 0) continue;

      // Skip if contains an <img> with non-empty alt (the alt is the accessible name)
      var imgs = el.querySelectorAll('img[alt]');
      var hasMeaningfulAltImg = false;
      for (var j = 0; j < imgs.length; j++) {
        if ((imgs[j].getAttribute('alt') || '').trim().length > 0) {
          hasMeaningfulAltImg = true;
          break;
        }
      }
      if (hasMeaningfulAltImg) continue;

      // Try to derive a label from common patterns
      var label = deriveLabel(el);
      if (label) {
        el.setAttribute('aria-label', label);
      }
    }
  }

  function deriveLabel(el) {
    var href = el.getAttribute('href') || '';
    var dataTitle = el.getAttribute('data-title') || el.getAttribute('title') || '';
    if (dataTitle.trim()) return dataTitle.trim();

    // Common social / link patterns
    var hl = href.toLowerCase();
    if (hl.indexOf('instagram.com') > -1) return 'Instagram';
    if (hl.indexOf('facebook.com') > -1) return 'Facebook';
    if (hl.indexOf('twitter.com') > -1 || hl.indexOf('x.com') > -1) return 'Twitter / X';
    if (hl.indexOf('tiktok.com') > -1) return 'TikTok';
    if (hl.indexOf('youtube.com') > -1 || hl.indexOf('youtu.be') > -1) return 'YouTube';
    if (hl.indexOf('linkedin.com') > -1) return 'LinkedIn';
    if (hl.indexOf('pinterest.com') > -1) return 'Pinterest';
    if (hl.indexOf('mailto:') === 0) return 'Email';
    if (hl.indexOf('tel:') === 0) return 'Phone';
    if (hl === '/' || hl === '#' || hl.endsWith('/index.html')) return 'Home';

    // Look at SVG aria-label / title
    var svg = el.querySelector('svg');
    if (svg) {
      var svgAria = svg.getAttribute('aria-label') || '';
      if (svgAria.trim()) return svgAria.trim();
      var svgTitle = svg.querySelector('title');
      if (svgTitle && svgTitle.textContent.trim()) return svgTitle.textContent.trim();

      // Look at lucide / heroicons class names
      var cls = svg.getAttribute('class') || '';
      var m = cls.match(/lucide-([a-z0-9-]+)|hi-([a-z0-9-]+)|heroicon-([a-z0-9-]+)/i);
      if (m) {
        var name = (m[1] || m[2] || m[3] || '').replace(/-/g, ' ');
        if (name) return name.charAt(0).toUpperCase() + name.slice(1);
      }
    }

    // Buttons with class hints
    var elClass = (el.getAttribute('class') || '').toLowerCase();
    if (elClass.indexOf('close') > -1) return 'Close';
    if (elClass.indexOf('menu') > -1 || elClass.indexOf('hamburger') > -1) return 'Menu';
    if (elClass.indexOf('search') > -1) return 'Search';
    if (elClass.indexOf('cart') > -1 || elClass.indexOf('bag') > -1) return 'Cart';
    if (elClass.indexOf('account') > -1 || elClass.indexOf('user') > -1) return 'Account';
    if (elClass.indexOf('lang') > -1 || elClass.indexOf('locale') > -1) return 'Language';

    return null;
  }

  // 3. Ensure a <main> landmark exists. If body has un-landmarked top-level content, wrap it.
  function ensureMainLandmark() {
    var existingMain = document.querySelector('main, [role="main"]');
    if (existingMain) return;

    // Identify top-level body children that aren't already landmarks.
    var orphans = [];
    var children = document.body.children;
    var sawHeader = false;
    var sawFooter = false;
    for (var i = 0; i < children.length; i++) {
      var c = children[i];
      var tag = c.tagName.toLowerCase();
      if (tag === 'header' || c.getAttribute('role') === 'banner') { sawHeader = true; continue; }
      if (tag === 'footer' || c.getAttribute('role') === 'contentinfo') { sawFooter = true; continue; }
      if (tag === 'script' || tag === 'noscript' || tag === 'style' || tag === 'a' /* skip-link */) continue;
      orphans.push(c);
    }

    if (orphans.length === 0) return;

    var main = document.createElement('main');
    main.id = 'main';
    main.setAttribute('role', 'main');
    // Insert <main> before the first orphan
    document.body.insertBefore(main, orphans[0]);
    for (var k = 0; k < orphans.length; k++) {
      main.appendChild(orphans[k]);
    }
    // Now that main exists, retry skip-link
    addSkipLink();
  }

  // 4. Sync <html lang> from common language toggle stores.
  function syncHtmlLang() {
    var stored = null;
    try {
      stored = localStorage.getItem('lang') ||
               localStorage.getItem('hv-lang') ||
               localStorage.getItem('language') ||
               localStorage.getItem('locale');
    } catch (e) { /* private mode */ }

    if (stored && (stored === 'kr' || stored === 'ko' || stored === 'ko-KR')) {
      document.documentElement.lang = 'ko';
    } else if (stored && (stored === 'en' || stored === 'en-US')) {
      document.documentElement.lang = 'en';
    }
    // Also wire to language-toggle clicks if present
    var togglers = document.querySelectorAll('[data-lang-toggle], .lang-toggle, [data-language-toggle]');
    for (var i = 0; i < togglers.length; i++) {
      togglers[i].addEventListener('click', function () {
        // Defer one tick so the site's own handler runs first
        setTimeout(syncHtmlLang, 50);
      });
    }
  }

  // 5. <iframe> needs a title for screen readers.
  function labelIframes() {
    var frames = document.querySelectorAll('iframe:not([title])');
    for (var i = 0; i < frames.length; i++) {
      var src = frames[i].getAttribute('src') || '';
      var label = 'Embedded content';
      if (src.indexOf('youtube') > -1 || src.indexOf('youtu.be') > -1) label = 'YouTube video';
      if (src.indexOf('vimeo') > -1) label = 'Vimeo video';
      if (src.indexOf('google.com/maps') > -1 || src.indexOf('maps.google') > -1) label = 'Google Map';
      if (src.indexOf('instagram.com') > -1) label = 'Instagram embed';
      if (src.indexOf('spotify') > -1) label = 'Spotify player';
      frames[i].setAttribute('title', label);
    }
  }

  // 6. Carousels / scrollable regions need to be keyboard-focusable.
  function makeScrollableRegionsFocusable() {
    var regions = document.querySelectorAll('[data-scrollable], .carousel, .scroll-region, [class*="overflow-x-auto"], [class*="overflow-x-scroll"]');
    for (var i = 0; i < regions.length; i++) {
      var r = regions[i];
      if (r.hasAttribute('tabindex')) continue;
      // Only add if it actually scrolls
      if (r.scrollWidth > r.clientWidth || r.scrollHeight > r.clientHeight) {
        r.setAttribute('tabindex', '0');
        if (!r.hasAttribute('aria-label') && !r.hasAttribute('aria-labelledby')) {
          r.setAttribute('aria-label', 'Scrollable region');
        }
      }
    }
  }

  // Boot — wait for DOM then run, plus a follow-up pass after async content lands.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  // Re-run once after 1500ms to catch SPA hydration / lazy components
  setTimeout(run, 1500);
})();
